/**
 * Reusable Quiz Component that works with the funnel configuration system
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChevronRight, ChevronLeft, CheckCircle2, Play } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useFunnelConfig } from '../hooks/use-funnel-config';
import { QuizQuestion } from '../config/types';
import { GHLMiddleware, createGHLMiddleware } from '../services/ghl-middleware';
import { createBookingProvider } from '../services/booking-providers';

interface FunnelQuizProps {
  onComplete: (formData: Record<string, any>, contactId?: string) => void;
  inline?: boolean;
}

// Validate US phone number
const validateUSPhoneNumber = (phone: string): { isValid: boolean; error?: string } => {
  const digitsOnly = phone.replace(/\D/g, '');
  
  if (!digitsOnly) {
    return { isValid: false, error: 'Phone number is required' };
  }
  
  if (digitsOnly.length === 11 && digitsOnly[0] === '1') {
    const tenDigits = digitsOnly.slice(1);
    return validatePhoneDigits(tenDigits);
  } else if (digitsOnly.length === 10) {
    return validatePhoneDigits(digitsOnly);
  } else {
    return { isValid: false, error: 'Please enter a valid 10-digit US phone number' };
  }
};

const validatePhoneDigits = (digits: string): { isValid: boolean; error?: string } => {
  if (digits[0] === '0' || digits[0] === '1') {
    return { isValid: false, error: 'Area code cannot start with 0 or 1' };
  }
  
  if (digits[3] === '0' || digits[3] === '1') {
    return { isValid: false, error: 'Invalid phone number format' };
  }
  
  return { isValid: true };
};

// Format phone number as user types
const formatPhoneNumber = (value: string): string => {
  const digitsOnly = value.replace(/\D/g, '');
  const limitedDigits = digitsOnly.slice(0, 11);
  
  if (limitedDigits.length === 0) {
    return '';
  } else if (limitedDigits.length <= 3) {
    return `(${limitedDigits}`;
  } else if (limitedDigits.length <= 6) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  } else if (limitedDigits.length <= 10) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
  } else {
    const tenDigits = limitedDigits.slice(1);
    return `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`;
  }
};

export function FunnelQuiz({ onComplete, inline = false }: FunnelQuizProps) {
  const navigate = useNavigate();
  const config = useFunnelConfig();
  const { toast } = useToast();
  
  const [visibleStep, setVisibleStep] = useState(0); // Step index in visible questions
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [contactId, setContactId] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [phoneErrors, setPhoneErrors] = useState<Record<string, string>>({});
  
  // Filter questions based on conditional logic
  const getVisibleQuestions = (): QuizQuestion[] => {
    return config.quiz.questions.filter((question) => {
      if (!question.conditional) return true;
      
      const { dependsOn, showWhen } = question.conditional;
      const dependentValue = formData[dependsOn];
      
      if (Array.isArray(showWhen)) {
        return showWhen.includes(dependentValue);
      }
      return dependentValue === showWhen;
    });
  };
  
  // Get visible questions - recalculate when formData changes (for conditional questions)
  const visibleQuestions = getVisibleQuestions();
  
  // Ensure visibleStep is within bounds
  const safeVisibleStep = Math.min(visibleStep, Math.max(0, visibleQuestions.length - 1));
  const currentQuestion = visibleQuestions[safeVisibleStep];
  const totalSteps = visibleQuestions.length;
  
  // Initialize GHL middleware if configured
  const ghlMiddleware = config.ghl
    ? createGHLMiddleware(config.ghl)
    : null;

  const validateField = (question: QuizQuestion, value: any): boolean | string => {
    if (question.required && (!value || value === '')) {
      return `${question.label} is required`;
    }

    if (question.type === 'email' && value) {
      const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      if (!emailValid) {
        return 'Please enter a valid email address';
      }
    }

    if (question.type === 'phone' && value) {
      const phoneValidation = validateUSPhoneNumber(value);
      if (!phoneValidation.isValid) {
        return phoneValidation.error || 'Invalid phone number';
      }
    }

    if (question.validation) {
      if (question.validation.pattern && value) {
        const regex = new RegExp(question.validation.pattern);
        if (!regex.test(value)) {
          return `Invalid format for ${question.label}`;
        }
      }

      if (question.validation.minLength && value.length < question.validation.minLength) {
        return `${question.label} must be at least ${question.validation.minLength} characters`;
      }

      if (question.validation.maxLength && value.length > question.validation.maxLength) {
        return `${question.label} must be no more than ${question.validation.maxLength} characters`;
      }

      if (question.validation.custom) {
        const customResult = question.validation.custom(value);
        if (customResult !== true) {
          return typeof customResult === 'string' ? customResult : `Invalid ${question.label}`;
        }
      }
    }

    return true;
  };

  const handleNext = async () => {
    if (!currentQuestion) return;
    
    // Validate current step
    const value = formData[currentQuestion.id];
    const validation = validateField(currentQuestion, value);
    
    if (validation !== true) {
      toast({
        title: 'Validation Error',
        description: typeof validation === 'string' ? validation : 'Please complete this field',
        variant: 'destructive',
      });
      return;
    }

    // Find the index of current question in the full questions array
    const currentQuestionIndex = config.quiz.questions.findIndex(q => q.id === currentQuestion.id);
    
    // Save to GHL if configured and we have contact info (after first 4 questions: name, email, phone, province)
    if (ghlMiddleware && visibleStep >= 3 && (formData.email || formData.phone)) {
      setIsSaving(true);
      try {
        const contact = {
          firstName: formData.firstName || '',
          lastName: formData.lastName || '',
          email: formData.email || '',
          phone: formData.phone || '',
        };

        const tags = config.ghl?.contactCreatedTags || [];
        const response = await ghlMiddleware.saveContact(contact, formData, tags);
        
        if (response.contactId) {
          setContactId(response.contactId);
        }
      } catch (error) {
        console.error('Error saving contact to GHL:', error);
      }
      setIsSaving(false);
    }

    // Move to next step or complete
    // Recalculate visible questions after form data update (for conditional questions)
    // This ensures conditional questions appear/disappear correctly
    const updatedVisibleQuestions = getVisibleQuestions();
    
    if (safeVisibleStep < updatedVisibleQuestions.length - 1) {
      // Move to next visible question
      setVisibleStep(safeVisibleStep + 1);
    } else {
      await handleComplete();
    }
  };

  const handleComplete = async () => {
    // Final save to GHL
    let finalContactId = contactId;
    
    if (ghlMiddleware) {
      setIsSaving(true);
      try {
        const contact = {
          firstName: formData.firstName || '',
          lastName: formData.lastName || '',
          email: formData.email || '',
          phone: formData.phone || '',
        };

        const tags = config.ghl?.quizCompleteTags || config.ghl?.defaultTags || [];
        const response = await ghlMiddleware.saveContact(contact, formData, tags);
        finalContactId = response.contactId;
      } catch (error) {
        console.error('Error in final GHL update:', error);
      }
      setIsSaving(false);
    }

    // Call completion handler
    onComplete(formData, finalContactId);
  };

  const handleBack = () => {
    if (safeVisibleStep > 0) {
      setVisibleStep(safeVisibleStep - 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, question: QuizQuestion) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNext();
    }
  };

  const renderQuestion = (question: QuizQuestion) => {
    const value = formData[question.id] || '';
    const error = phoneErrors[question.id];

    switch (question.type) {
      case 'text':
      case 'email':
        return (
          <div className="space-y-2">
            <Label htmlFor={question.id} className="block font-medium text-left text-white">
              {question.label} {question.required && <span className="text-red-400">*</span>}
            </Label>
            <input
              id={question.id}
              type={question.type}
              required={question.required}
              placeholder={question.placeholder}
              value={value}
              onChange={(e) => {
                setFormData({ ...formData, [question.id]: e.target.value });
                if (error) setPhoneErrors({ ...phoneErrors, [question.id]: '' });
              }}
              onKeyDown={(e) => handleKeyDown(e, question)}
              className="w-full px-4 py-3 rounded-lg border bg-slate-700 text-white border-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 placeholder:text-slate-400"
            />
            {error && <p className="text-sm text-red-400 mt-1">{error}</p>}
          </div>
        );

      case 'phone':
        return (
          <div className="space-y-2">
            <Label htmlFor={question.id} className="block font-medium text-left text-white">
              {question.label} {question.required && <span className="text-red-400">*</span>}
            </Label>
            <input
              id={question.id}
              type="tel"
              required={question.required}
              placeholder={question.placeholder || '(555) 123-4567'}
              value={value}
              onChange={(e) => {
                const formatted = formatPhoneNumber(e.target.value);
                setFormData({ ...formData, [question.id]: formatted });
                const validation = validateUSPhoneNumber(formatted);
                if (validation.error) {
                  setPhoneErrors({ ...phoneErrors, [question.id]: validation.error });
                } else {
                  setPhoneErrors({ ...phoneErrors, [question.id]: '' });
                }
              }}
              onKeyDown={(e) => handleKeyDown(e, question)}
              className="w-full px-4 py-3 rounded-lg border bg-slate-700 text-white border-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 placeholder:text-slate-400"
            />
            {error && <p className="text-sm text-red-400 mt-1">{error}</p>}
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-4">
            <Label className="block font-medium text-left mb-4 text-white">
              {question.label} {question.required && <span className="text-red-400">*</span>}
            </Label>
            <RadioGroup
              value={value}
              onValueChange={(newValue) => setFormData({ ...formData, [question.id]: newValue })}
            >
              <div className="grid grid-cols-2 gap-3">
                {question.options?.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center space-x-2 p-4 rounded-lg cursor-pointer border hover:bg-slate-700 border-slate-600 bg-slate-800"
                  >
                    <RadioGroupItem value={option.value} id={`${question.id}-${option.value}`} className="text-amber-400" />
                    <Label htmlFor={`${question.id}-${option.value}`} className="flex-1 cursor-pointer text-white">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </div>
        );

      case 'select':
        return (
          <div className="space-y-2">
            <Label htmlFor={question.id} className="block font-medium text-left text-white">
              {question.label} {question.required && <span className="text-red-400">*</span>}
            </Label>
            <Select
              value={value || ''}
              onValueChange={(newValue) => setFormData({ ...formData, [question.id]: newValue })}
            >
              <SelectTrigger className="w-full bg-slate-700 text-white border-slate-600">
                <SelectValue placeholder={question.placeholder || 'Select an option'} />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                {question.options?.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="text-white hover:bg-slate-700">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'textarea':
        return (
          <div className="space-y-2">
            <Label htmlFor={question.id} className="block font-medium text-left text-white">
              {question.label} {question.required && <span className="text-red-400">*</span>}
            </Label>
            <textarea
              id={question.id}
              required={question.required}
              placeholder={question.placeholder}
              value={value}
              onChange={(e) => setFormData({ ...formData, [question.id]: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border bg-slate-700 text-white border-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 min-h-[100px] placeholder:text-slate-400"
            />
          </div>
        );

      default:
        return null;
    }
  };

  const isStepValid = () => {
    if (!currentQuestion) return false;
    const value = formData[currentQuestion.id];
    const validation = validateField(currentQuestion, value);
    return validation === true;
  };

  if (!currentQuestion) {
    return (
      <div className="text-center p-8">
        <p className="text-slate-300">Loading questions...</p>
      </div>
    );
  }

  const quizContent = (
    <div className="max-w-2xl mx-auto">
      <Card className={inline ? "bg-slate-800 border border-slate-700 p-6 md:p-8 shadow-xl" : "bg-slate-800 border border-slate-700 p-8 shadow-xl"}>
        {/* Progress Bar */}
        {config.quiz.showProgress && totalSteps > 0 && (
          <div className="mb-8">
            <div className="flex justify-end mb-2">
              <span className="text-sm font-medium text-amber-400">
                {Math.round(((safeVisibleStep + 1) / totalSteps) * 100)}% Complete
              </span>
            </div>
            <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-500 to-amber-600 transition-all duration-500 ease-out"
                style={{ width: `${((safeVisibleStep + 1) / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Question */}
        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
          {renderQuestion(currentQuestion)}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-slate-700">
          {safeVisibleStep > 0 && (
            <Button
              onClick={handleBack}
              variant="outline"
              disabled={isSaving}
              className="flex items-center gap-2 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
          )}
          
          <Button
            onClick={handleNext}
            disabled={!isStepValid() || isSaving}
            className="ml-auto flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
          >
            {safeVisibleStep === totalSteps - 1 ? (
              <>
                {isSaving ? 'Saving...' : 'Continue'}
                <CheckCircle2 className="w-4 h-4" />
              </>
            ) : (
              <>
                {isSaving ? 'Saving...' : 'Continue'}
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </Card>

      {!inline && (
        <p className="text-center text-sm text-slate-400 mt-6">
          🔒 Your information is secure • Get instant results • No obligation
        </p>
      )}
    </div>
  );

  if (inline) {
    return quizContent;
  }

  return (
    <section id="quiz" className="py-20 bg-slate-900">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white">
            Complete Your Assessment
          </h2>
        </div>
        {quizContent}
      </div>
    </section>
  );
}
